import {
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title
} from "@mantine/core";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api.js";

interface Customer {
  workspaceId: string;
  name: string;
  planKey: string | null;
  status: string | null;
  billingEmail: string | null;
  ownerEmail: string | null;
  createdAt: string;
}

const statusColor = (status: string) => {
  if (status === "active") return "teal";
  if (status === "trial") return "blue";
  if (status === "churned") return "red";
  return "gray";
};

export const CustomersListPage = () => {
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["internal", "customers"],
    queryFn: () => api.get<{ customers: Customer[] }>("/internal/customers")
  });

  const customers = (data?.customers ?? []).filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.billingEmail ?? "").toLowerCase().includes(search.toLowerCase()) ||
      (c.ownerEmail ?? "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Customers</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          component={Link as React.ElementType}
          to="/customers/new"
        >
          New customer
        </Button>
      </Group>

      <TextInput
        placeholder="Search by name or email..."
        leftSection={<IconSearch size={14} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        style={{ maxWidth: 360 }}
      />

      <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
        {isLoading ? (
          <Group justify="center" p="xl">
            <Loader size="sm" />
          </Group>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Plan</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Owner</Table.Th>
                <Table.Th>Created</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {customers.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c="dimmed" ta="center" p="md" size="sm">
                      No customers found
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                customers.map((c) => (
                  <Table.Tr key={c.workspaceId}>
                    <Table.Td>
                      <Text
                        component={Link as React.ElementType}
                        to={`/customers/${c.workspaceId}`}
                        size="sm"
                        fw={500}
                        c="brand"
                      >
                        {c.name}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" variant="outline">
                        {c.planKey ?? "free"}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" color={statusColor(c.status ?? "")}>
                        {c.status ?? "new"}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {c.ownerEmail ?? c.billingEmail ?? "—"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {new Date(c.createdAt).toLocaleDateString()}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
};
